import { useState, type ReactNode } from 'react';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils/cn';
import { routePaths } from '@/app/routes';
import { useLogout } from '@/features/auth/hooks/useLogout';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { useEnsureUserProfile } from '@/features/auth/hooks/useUserProfile';
import { useNomenclatureSync } from '@/features/nomenclature/hooks/useNomenclatureSync';
import { usePatients } from '@/features/patients/hooks/usePatients';
import { PatientSwitcher } from '@/features/patients/components/PatientSwitcher';
import { ContentLoader } from '@/components/feedback/ContentLoader';
import { ErrorState } from '@/components/feedback/ErrorState';
import { MaintenanceBanner } from '@/features/maintenance/components/MaintenanceBanner';
import { SiteHeader } from './SiteHeader';

interface NavItem {
  to: string;
  key: string;
  end: boolean;
  icon: ReactNode;
}

const HomeIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
    <path d="M3 10.5 12 3l9 7.5M5 9.5V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const CalendarIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
    <rect x="3" y="4.5" width="18" height="16" rx="2" /><path d="M3 9h18M8 3v3M16 3v3" strokeLinecap="round" />
  </svg>
);
const NewAppointmentIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
    <rect x="3" y="4.5" width="18" height="16" rx="2" /><path d="M3 9h18M8 3v3M16 3v3M12 13.5v4M10 15.5h4" strokeLinecap="round" />
  </svg>
);
const ResultsIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" strokeLinejoin="round" /><path d="M14 3v5h5M9 13h6M9 17h4" strokeLinecap="round" />
  </svg>
);
const AccountsIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
    <path d="M16 8a4 4 0 1 0-8 0 4 4 0 0 0 8 0zM4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const PatientsIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" strokeLinecap="round" strokeLinejoin="round" /><circle cx="9" cy="7" r="4" />
  </svg>
);

const navItems: NavItem[] = [
  { to: routePaths.home, key: 'nav.home', end: true, icon: HomeIcon },
  { to: routePaths.newAppointment, key: 'nav.newAppointment', end: false, icon: NewAppointmentIcon },
  { to: routePaths.appointments, key: 'nav.appointments', end: false, icon: CalendarIcon },
  { to: routePaths.results, key: 'nav.results', end: false, icon: ResultsIcon },
  { to: routePaths.attachedAccounts, key: 'nav.attachedAccounts', end: false, icon: AccountsIcon },
];

/** Shared horizontal frame — the whole site is constrained to this width. */
const FRAME = 'mx-auto w-full max-w-[1440px]';

/** NavLink styling shared by the main navigation and the footer links. */
const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'flex items-center gap-3 rounded-input px-3 py-2.5 text-sm font-semibold transition-colors',
    isActive
      ? 'bg-white/10 text-white shadow-[inset_3px_0_0_0_var(--color-accent)]'
      : 'text-white/70 hover:bg-white/5 hover:text-white',
  );

/** Same row as a NavLink, but inert and muted — used while the account has no patient. */
const lockedNavClass =
  'flex cursor-not-allowed select-none items-center gap-3 rounded-input px-3 py-2.5 text-sm font-semibold text-white/30';

/**
 * Authenticated app shell: the gralmedical.ro-styled top header (SiteHeader)
 * over a left account sidebar + content. Everything is constrained to a shared
 * horizontal frame. The sidebar is static on desktop and a drawer on mobile.
 */
export function AppLayout() {
  const { t } = useTranslation();
  const logout = useLogout();
  const user = useAuthStore((s) => s.user);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Logged-in identity shown above the logout button (mirrors the mobile menu).
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
  const initials =
    (fullName
      ? fullName
          .split(/\s+/)
          .map((p) => p[0])
          .slice(0, 2)
          .join('')
      : user?.email?.[0] ?? '?'
    ).toUpperCase();

  // Make sure the identity block above has a profile to show — covers a
  // login whose /me call failed, without requiring a manual page refresh.
  useEnsureUserProfile();

  // Keep the Azure nomenclature cache fresh in the background while signed in.
  useNomenclatureSync();

  // The patient list is the prerequisite for every page's data; gate the
  // content area on it so pages don't flash an empty state first.
  const patients = usePatients();

  // A fresh account has no patient yet, and nothing in the app works without
  // one: the shell sends the user straight to the add-patient screen and keeps
  // every navigation entry inert until the first patient exists — only
  // "Deconectare" stays live. The lock lifts by itself once the list refetches
  // with a patient in it (useAddPatient awaits that refetch).
  const noPatient = patients.isSuccess && patients.data.length === 0;
  const location = useLocation();

  // useLogout tears down local state and redirects to the auth server's /logout
  // (which ends the web session and returns to /login).
  const onLogout = () => logout.mutate();

  // One navigation entry: a live link, or — while the account has no patient —
  // an inert, muted row whose tooltip says why it does nothing.
  const renderNav = (to: string, label: string, icon: ReactNode, end = false) =>
    noPatient ? (
      <span key={to} aria-disabled="true" title={t('nav.locked')} className={lockedNavClass}>
        {icon}
        {label}
      </span>
    ) : (
      <NavLink key={to} to={to} end={end} onClick={() => setDrawerOpen(false)} className={navLinkClass}>
        {icon}
        {label}
      </NavLink>
    );

  const sidebar = (
    <>
      {/* Active patient context — above the navigation. */}
      <div className="border-b border-white/10 p-3">
        <PatientSwitcher variant="sidebar" />
      </div>
      <nav className="flex flex-1 flex-col gap-1 p-3">
        {navItems.map((item) => renderNav(item.to, t(item.key), item.icon, item.end))}
      </nav>
      <div className="border-t border-white/10 p-3">
        {user?.email && (
          <div className="mb-2 flex items-center gap-3 px-3 py-2">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-bold text-white">
              {initials}
            </span>
            <div className="min-w-0">
              {fullName && <p className="truncate text-sm font-semibold text-white">{fullName}</p>}
              <p className="truncate text-xs text-white/60">{user.email}</p>
            </div>
          </div>
        )}
        {/* Patient accounts on this login — kept with the identity, above logout. */}
        {renderNav(routePaths.patients, t('nav.patients'), PatientsIcon)}
        <button
          type="button"
          onClick={onLogout}
          disabled={logout.isPending}
          className="flex w-full items-center gap-3 rounded-input px-3 py-2.5 text-sm font-semibold text-white/80 transition-colors hover:bg-white/5 hover:text-white disabled:opacity-50"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {t('nav.logout')}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-dvh bg-canvas">
      <div className={FRAME}>
        <SiteHeader onMenuClick={() => setDrawerOpen(true)} />

        <div className="flex gap-6 px-4 pt-6 sm:px-6">
          {/* Desktop sidebar — independent, viewport-tall, sticky under the
              header; logout pinned at its bottom (not pushed by long content). */}
          <aside className="sticky top-[9.5rem] hidden h-[calc(100dvh-11rem)] w-60 shrink-0 flex-col self-start overflow-y-auto rounded-card bg-inverse text-white lg:flex">
            {sidebar}
          </aside>

          {/* Mobile drawer */}
          {drawerOpen && (
            <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true">
              <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} aria-hidden="true" />
              <aside className="absolute inset-y-0 left-0 flex w-64 flex-col bg-inverse text-white">
                <div className="flex h-16 items-center justify-between border-b border-white/10 px-4">
                  <span className="text-sm font-bold uppercase tracking-wide text-white/90">{t('nav.account')}</span>
                  <button type="button" onClick={() => setDrawerOpen(false)} aria-label="Închide" className="rounded-input p-1.5 text-white/70 hover:bg-white/10">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
                      <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>
                {sidebar}
              </aside>
            </div>
          )}

          <main className="min-w-0 flex-1 pb-6">
            {/* Upcoming / per-section maintenance notices (whole-app maintenance is gated above the router). */}
            <MaintenanceBanner />
            {patients.isLoading ? (
              <ContentLoader label={t('common.loadingPatients')} />
            ) : patients.isError ? (
              <ErrorState description={t('common.load_error')} onRetry={() => void patients.refetch()} />
            ) : noPatient && location.pathname !== routePaths.addPatient ? (
              // Nothing to show without a patient: straight to the add-patient screen.
              <Navigate to={routePaths.addPatient} replace />
            ) : (
              <Outlet />
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
